<template>
  <div>
    <!-- this extra div has to be here, if not the edit toggle wont re-open after save -->
    <Wrapper :client="client">
      <!-- Description list -->
      <div class="relative mx-auto mt-6 max-w-5xl px-4 sm:px-6 lg:px-8">
        <div class="absolute right-8 top-0">
          <button
            v-if="!editing"
            type="button"
            class="inline-flex items-center justify-center rounded-md border border-warning bg-warning px-2 py-1 text-xs font-medium text-white shadow-sm hover:bg-warning-dark focus:outline-none focus:ring-2 focus:ring-yellow-700 focus:ring-offset-2"
            @click="editing = true">
            <svg
              class="-ml-1 mr-2 size-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span>Edit</span>
          </button>

          <div
            v-else
            class="flex flex-col space-y-1 sm:flex-row sm:space-x-1 sm:space-y-0">
            <button
              v-if="saving"
              type="button"
              class="inline-flex justify-center rounded-md border border-green-100 bg-success px-2 py-1 text-xs font-medium text-white shadow-sm hover:bg-success-dark focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2">
              <svg
                class="-mx-1 size-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button
              v-else
              type="button"
              class="inline-flex justify-center rounded-md border border-green-200 bg-success px-2 py-1 text-xs font-medium text-white shadow-sm hover:bg-success-dark focus:outline-none focus:ring-2 focus:ring-success-dark focus:ring-offset-2"
              @click="save">
              <svg
                class="-mx-1 size-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button
              type="button"
              class="inline-flex justify-center rounded-md border border-red-200 bg-warning px-2 py-1 text-xs font-medium text-white shadow-sm hover:bg-warning-dark focus:outline-none focus:ring-2 focus:ring-warning-dark focus:ring-offset-2"
              @click="cancelEdit">
              <svg
                class="-mx-1 size-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <template />
          <div
            v-if="editing"
            class="sm:col-span-2">
            <dt class="text-sm font-medium text-font">
              Name
            </dt>
            <dd class="mt-1 text-sm text-font-darkest">
              <input
                id="name"
                v-model="form.name"
                type="text"
                name="name"
                class="block w-full max-w-lg rounded-md border-font-lightest p-1 shadow-sm focus:border-primary focus:ring-primary sm:max-w-xs sm:text-sm"
                :class="{'border-red-300': form.errors.name }">
              <p
                v-if="form.errors.name"
                class="absolute -ml-3 mt-2 text-xs text-red-600">
                {{ form.errors.name }}
              </p>
            </dd>
          </div>
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-font">
              Client contact
            </dt>
            <dd class="mt-1 text-sm text-font-darkest">
              <select
                v-if="editing"
                id="creator"
                v-model="form.primary_contact"
                class="block w-full max-w-lg rounded-md border-font-lightest p-1 shadow-sm focus:border-primary focus:ring-primary sm:max-w-xs sm:text-sm"
                @change="onPrimaryContactChanged">
                <option
                  v-for="option in client.contacts"
                  :value="option.id">
                  {{ option.name }}
                </option>
                <option
                  value="addnew"
                  class="font-semibold text-primary">
                  + Add New Client Contact
                </option>
              </select>
              <p v-else-if="client.contact !== null">
                {{ client.contact.name }}
              </p>
              <i
                v-else
                class="text-font">No primary contact selected</i>
            </dd>
          </div>
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-font">
              Key account manager
            </dt>
            <dd class="mt-1 text-sm text-font-darkest">
              <select
                v-if="editing"
                id="creator"
                v-model="form.key_account_manager"
                class="block w-full max-w-lg rounded-md border-font-lightest p-1 shadow-sm focus:border-primary focus:ring-primary sm:max-w-xs sm:text-sm">
                <option
                  v-for="option in team_members"
                  :value="option.value">
                  {{ option.text }}
                </option>
              </select>

              <p v-else-if="client.kam !== null">
                {{ client.kam.name }}
              </p>
              <i
                v-else
                class="text-font">Not defined</i>
            </dd>
          </div>

          <div class="sm:col-span-2">
            <dt class="text-sm font-medium text-font">
              Comment
            </dt>
            <dd class="mt-1 max-w-prose text-sm">
              <div v-if="editing">
                <textarea
                  id="about"
                  v-model="form.comment"
                  name="about"
                  rows="4"
                  class="block w-full rounded-md border border-font-lightest p-1 text-sm text-font-darkest shadow-sm focus:border-primary focus:ring-primary"
                  placeholder="Add your comment..." />
              </div>
              <p
                v-else-if="client.comment !== null"
                class="text-font-darkest">
                {{ client.comment }}
              </p>
              <p
                v-else
                class="text-gray-400">
                No comment added
              </p>
            </dd>
          </div>
        </dl>
      </div>

      <!-- Client feed -->
      <div class="mx-auto mt-8 max-w-5xl px-4 pb-12 sm:px-6 lg:px-8">
        <div class="mt-1 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <h2 class="text-sm font-medium text-font">
            Recent projects
          </h2>
          <h2 class="text-sm font-medium text-font">
            Recent activity
          </h2>
        </div>

        <div class="mt-1 grid grid-cols-1 gap-8 sm:grid-cols-2">
          <!-- Client projects -->
          <SimpleCardsWithStatus
            :items="client.recent_projects"
            route-name="projects.show" />

          <!-- Client activity -->
          <div class="mt-3">
            <ul>
              <li v-for="(activity,key) in activities">
                <div class="relative pb-8">
                  <span
                    v-if="key < activities.length-1"
                    class="absolute left-5 top-5 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true" />
                  <div class="relative flex items-start space-x-3">
                    <div class="relative">
                      <img
                        class="flex size-10 items-center justify-center rounded-full bg-gray-400 object-cover ring-8 ring-white"
                        :src="activity.creator.profile_photo_url"
                        :alt="activity.creator.name">

                      <span class="absolute -bottom-0.5 -right-1 rounded-tl bg-white px-0.5 py-px">
                        <ActivityIcon
                          :type="activity.type"
                          class="text-font-darkest" />
                      </span>
                    </div>
                    <div class="min-w-0 flex-1">
                      <div>
                        <div class="text-sm">
                          <Link
                            v-if="activity.project"
                            :href="route('projects.show', {project: activity.project})"
                            class="font-medium text-font-darkest">
                            {{ activity.creator.name }} in <span class="text-indigo-950 underline">{{ activity.project.name }}</span>
                          </Link>
                          <div
                            v-else
                            class="font-medium text-font-darkest">
                            {{ activity.creator.name }}
                          </div>
                          <span
                            v-if="activity.contact !== null"
                            class="text-xs text-font"> with <a
                            href="#"
                            class="font-medium text-font-darkest">{{ activity.contact.name }}</a></span>
                        </div>
                        <p class="mt-0.5 text-sm text-font">
                          <date-time-formatter :date-time="activity.created_at" />
                        </p>
                      </div>
                      <div class="mt-2 text-sm text-gray-700">
                        <p>
                          {{ activity.comment }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>

            <div
              v-if="activities.length === 0"
              class="flex flex-1 items-center justify-between truncate rounded-md border border-gray-100 bg-white">
              <div class="flex-1 truncate px-4 py-2 text-center text-sm">
                <a
                  href="#"
                  class="font-medium text-font-lightest hover:text-gray-600">No activities on this client yet</a>
              </div>
            </div>
            <Link
              v-else-if="activities.length === 5"
              :href="route('clients.activities', {client: client})"
              class="block w-full rounded-md border border-font-darkest p-2 text-center text-font-darkest">
              See all
            </Link>
          </div>
        </div>
      </div>
    </Wrapper>
  </div>
  <CreateClientContact
    :show="showCreateFrom"
    :client="client"
    @close="showCreateFrom = false"
    @new-primary-contact="primaryContactUpdate" />
</template>

<script>
import { Link } from '@inertiajs/vue3';
import DateTimeFormatter from '@/Components/DateTimeFormatter.vue';
import ActivityIcon from '@/Components/ActivityIcon.vue';
import SimpleCardsWithStatus from '@/Components/Cards/SimpleCardsWithStatus.vue';
import StatusBadge from '../../ComponentsDeprecated/StatusBadge.vue';
import CreateClientContact from '../../ComponentsDeprecated/Modal/CreateClientContact.vue';
import Wrapper from './Wrapper.vue';

export default {
  name: 'OverviewTab',
  components: {
    SimpleCardsWithStatus,
    ActivityIcon,
    DateTimeFormatter,
    Wrapper,
    CreateClientContact,
    StatusBadge,
    Link,
  },

  props: {
    client: Object,
    activities: Array,
    client_id: Object,
    projects: Object,
  },

  data() {
    return {
      showCreateFrom: false,
      editing: false,
      saving: false,
      team_members: [],
      form: this.$inertia.form({
        name: this.client.name,
        comment: this.client.comment,
        key_account_manager: this.client.key_account_manager,
        primary_contact: this.client.primary_contact,
      }),
    };
  },

  computed: {
    recentlySuccessful() {
      return this.form.recentlySuccessful;
    },
  },

  watch: {
    recentlySuccessful(val, oldVal) {
      if (val === true) {
        this.editing = false;
      }
    },
  },

  created() {
    this.team_members.push({
      value: this.$page.props.team.owner.id, text: this.$page.props.team.owner.name,
    });
    for (let i = 0; i < this.$page.props.team.users.length; i++) {
      this.team_members.push({
        value: this.$page.props.team.users[i].id, text: this.$page.props.team.users[i].name,
      });
    }
  },

  methods: {
    cancelEdit() {
      this.editing = false;
      this.form.name = this.client.name;
      this.form.comment = this.client.comment;
      this.form.created_by = this.client.created_by;
      this.form.clearErrors();
    },

    onPrimaryContactChanged() {
      if (this.form.primary_contact === 'addnew') {
        this.showCreateFrom = true;
        this.form.primary_contact = null;
      }
    },

    save() {
      this.saving = true;
      this.form.put(route('clients.update', { client: this.client }), {
        errorBag: 'updateClient',
        preserveScroll: true,
        onSuccess: () => {
          this.$notify({
            type: 'success',
            title: 'Updated',
            text: 'The client was updated',
          }, 4000);
        },
        onError: () => {
          this.$notify({
            type: 'danger',
            title: 'Something went wrong',
            text: 'Could not update the client',
          }, 4000);
        },
      });
      this.saving = false;
      this.editing = false;
    },

    primaryContactUpdate() {
      this.form.primary_contact = this.client.contacts[this.client.contacts.length - 1].id;
    },
  },
};
</script>

<style scoped>

</style>
